import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  useNavigate,
  useParams
} from 'react-router-dom';

import './AdminCourseForm.scss';

const DEFAULT_FORM = {
  course_name: '',
  num_holes: 18,

  tee_off_block: '',
  cart_option: '',
  rentals: '',
  restaurant: '',
  public_private: 'Public',

  city: '',
  province: 'Ontario',
  country: 'Canada',

  address: '',
  postal_code: '',

  course_image_path: '',
  course_website_link: '',

  latitude: '',
  longitude: ''
};

export default function AdminCourseForm() {
  const navigate = useNavigate();

  const {
    id
  } = useParams();

  const isEditing =
    Boolean(id);

  const apiBaseUrl = useMemo(() => {
    const envUrl =
      process.env.REACT_APP_API_URL ||
      '';

    return envUrl.endsWith('/')
      ? envUrl.slice(0, -1)
      : envUrl;
  }, []);

  const token =
    localStorage.getItem(
      'tg_admin_token'
    );

  const [
    form,
    setForm
  ] = useState(DEFAULT_FORM);

  const [
    loading,
    setLoading
  ] = useState(isEditing);

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  const [
    success,
    setSuccess
  ] = useState('');

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const fetchCourse =
      async () => {
        setLoading(true);
        setError('');

        try {
          const response =
            await fetch(
              `${apiBaseUrl}/admin/courses/${id}`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const data =
            await response.json();

          if (!response.ok) {
            throw new Error(
              data?.message ||
                'Unable to load course.'
            );
          }

          setForm({
            ...DEFAULT_FORM,
            ...data.course,

            latitude:
              data.course
                ?.latitude ??
              '',

            longitude:
              data.course
                ?.longitude ??
              ''
          });
        } catch (err) {
          setError(
            err.message ||
              'Unable to load course.'
          );
        } finally {
          setLoading(false);
        }
      };

    fetchCourse();
  }, [
    apiBaseUrl,
    id,
    isEditing,
    token
  ]);

  const handleChange = (
    event
  ) => {
    const {
      name,
      value
    } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (
      !form.course_name.trim()
    ) {
      return 'Course name is required.';
    }

    if (!form.city.trim()) {
      return 'City is required.';
    }

    if (
      !form.province.trim()
    ) {
      return 'Province is required.';
    }

    if (
      !form.country.trim()
    ) {
      return 'Country is required.';
    }

    return '';
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    const validationError =
      validateForm();

    if (validationError) {
      setError(
        validationError
      );

      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const url = isEditing
        ? `${apiBaseUrl}/admin/courses/${id}`
        : `${apiBaseUrl}/admin/courses`;

      const method = isEditing
        ? 'PATCH'
        : 'POST';

      const response =
        await fetch(
          url,
          {
            method,

            headers: {
              'Content-Type':
                'application/json',

              Authorization:
                `Bearer ${token}`
            },

            body: JSON.stringify(
              form
            )
          }
        );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data?.message ||
            'Unable to save course.'
        );
      }

      setSuccess(
        isEditing
          ? 'Course updated successfully.'
          : 'Course created successfully.'
      );

      if (
        !isEditing &&
        data.course?.course_id
      ) {
        navigate(
          `/admin/courses/${data.course.course_id}`,
          {
            replace: true
          }
        );
      } else if (
        data.course
      ) {
        setForm({
          ...DEFAULT_FORM,
          ...data.course
        });
      }
    } catch (err) {
      setError(
        err.message ||
          'Unable to save course.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-course-form-loading">
        Loading course...
      </div>
    );
  }

  return (
    <div className="admin-course-form-page">
      <section className="admin-course-form-heading">
        <div>
          <p className="admin-course-form-heading__eyebrow">
            Course Network
          </p>

          <h2>
            {isEditing
              ? 'Edit Course'
              : 'Add Course'}
          </h2>

          <p>
            Create and maintain the
            core course record used
            throughout The Tour Golfers.
          </p>
        </div>

        <button
          type="button"
          className="admin-course-form-heading__back"
          onClick={() =>
            navigate(
              '/admin/courses'
            )
          }
        >
          ← Back to Courses
        </button>
      </section>

      <form
        className="admin-course-form"
        onSubmit={
          handleSubmit
        }
      >
        {error ? (
          <div className="admin-course-form__error">
            {error}
          </div>
        ) : null}

        {success ? (
          <div className="admin-course-form__success">
            {success}
          </div>
        ) : null}

        <section className="admin-course-form-card">
          <div className="admin-course-form-card__header">
            <div>
              <span>
                01
              </span>

              <div>
                <h3>
                  Course Details
                </h3>

                <p>
                  Basic course
                  information.
                </p>
              </div>
            </div>
          </div>

          <div className="admin-course-form-grid">
            <div className="admin-course-form-field admin-course-form-field--full">
              <label htmlFor="course_name">
                Course Name *
              </label>

              <input
                id="course_name"
                name="course_name"
                value={
                  form.course_name
                }
                onChange={
                  handleChange
                }
                placeholder="Royal Ontario Golf Club"
              />
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="num_holes">
                Number of Holes
              </label>

              <select
                id="num_holes"
                name="num_holes"
                value={
                  form.num_holes
                }
                onChange={
                  handleChange
                }
              >
                <option value="9">
                  9
                </option>

                <option value="18">
                  18
                </option>

                <option value="27">
                  27
                </option>

                <option value="36">
                  36
                </option>
              </select>
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="public_private">
                Access Type
              </label>

              <select
                id="public_private"
                name="public_private"
                value={
                  form.public_private
                }
                onChange={
                  handleChange
                }
              >
                <option value="Public">
                  Public
                </option>

                <option value="Private">
                  Private
                </option>

                <option value="Semi-Private">
                  Semi-Private
                </option>

                <option value="Municipal">
                  Municipal
                </option>

                <option value="Resort">
                  Resort
                </option>
              </select>
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="tee_off_block">
                Tee-Off Block
              </label>

              <input
                id="tee_off_block"
                name="tee_off_block"
                value={
                  form.tee_off_block
                }
                onChange={
                  handleChange
                }
                placeholder="Front / Back / etc."
              />
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="cart_option">
                Cart Option
              </label>

              <input
                id="cart_option"
                name="cart_option"
                value={
                  form.cart_option
                }
                onChange={
                  handleChange
                }
                placeholder="Available / Included / Walking"
              />
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="rentals">
                Rentals
              </label>

              <input
                id="rentals"
                name="rentals"
                value={
                  form.rentals
                }
                onChange={
                  handleChange
                }
                placeholder="Club rentals available"
              />
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="restaurant">
                Restaurant
              </label>

              <input
                id="restaurant"
                name="restaurant"
                value={
                  form.restaurant
                }
                onChange={
                  handleChange
                }
                placeholder="Yes / No / Full service"
              />
            </div>
          </div>
        </section>

        <section className="admin-course-form-card">
          <div className="admin-course-form-card__header">
            <div>
              <span>
                02
              </span>

              <div>
                <h3>
                  Location
                </h3>

                <p>
                  Address and geographic
                  information.
                </p>
              </div>
            </div>
          </div>

          <div className="admin-course-form-grid">
            <div className="admin-course-form-field admin-course-form-field--full">
              <label htmlFor="address">
                Address
              </label>

              <input
                id="address"
                name="address"
                value={
                  form.address
                }
                onChange={
                  handleChange
                }
                placeholder="123 Golf Club Road"
              />
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="city">
                City *
              </label>

              <input
                id="city"
                name="city"
                value={
                  form.city
                }
                onChange={
                  handleChange
                }
                placeholder="Oakville"
              />
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="province">
                Province *
              </label>

              <input
                id="province"
                name="province"
                value={
                  form.province
                }
                onChange={
                  handleChange
                }
                placeholder="Ontario"
              />
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="country">
                Country *
              </label>

              <input
                id="country"
                name="country"
                value={
                  form.country
                }
                onChange={
                  handleChange
                }
                placeholder="Canada"
              />
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="postal_code">
                Postal Code
              </label>

              <input
                id="postal_code"
                name="postal_code"
                value={
                  form.postal_code
                }
                onChange={
                  handleChange
                }
                placeholder="L6J 1A1"
              />
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="latitude">
                Latitude
              </label>

              <input
                id="latitude"
                name="latitude"
                type="number"
                step="0.000001"
                value={
                  form.latitude
                }
                onChange={
                  handleChange
                }
                placeholder="43.450000"
              />
            </div>

            <div className="admin-course-form-field">
              <label htmlFor="longitude">
                Longitude
              </label>

              <input
                id="longitude"
                name="longitude"
                type="number"
                step="0.000001"
                value={
                  form.longitude
                }
                onChange={
                  handleChange
                }
                placeholder="-79.680000"
              />
            </div>
          </div>
        </section>

        <section className="admin-course-form-card">
          <div className="admin-course-form-card__header">
            <div>
              <span>
                03
              </span>

              <div>
                <h3>
                  Digital Profile
                </h3>

                <p>
                  Website and course
                  image information.
                </p>
              </div>
            </div>
          </div>

          <div className="admin-course-form-grid">
            <div className="admin-course-form-field admin-course-form-field--full">
              <label htmlFor="course_website_link">
                Course Website
              </label>

              <input
                id="course_website_link"
                name="course_website_link"
                type="url"
                value={
                  form.course_website_link
                }
                onChange={
                  handleChange
                }
                placeholder="https://..."
              />
            </div>

            <div className="admin-course-form-field admin-course-form-field--full">
              <label htmlFor="course_image_path">
                Course Image Path
              </label>

              <input
                id="course_image_path"
                name="course_image_path"
                value={
                  form.course_image_path
                }
                onChange={
                  handleChange
                }
                placeholder="/assets/images/courseImages/..."
              />
            </div>
          </div>
        </section>

        <div className="admin-course-form-actions">
          <button
            type="button"
            className="admin-course-form-actions__secondary"
            onClick={() =>
              navigate(
                '/admin/courses'
              )
            }
            disabled={
              saving
            }
          >
            Cancel
          </button>

          <button
            type="submit"
            className="admin-course-form-actions__primary"
            disabled={
              saving
            }
          >
            {saving
              ? 'Saving...'
              : isEditing
              ? 'Save Changes'
              : 'Create Course'}
          </button>
        </div>
      </form>
    </div>
  );
}